/**
 * AdminBracketEditPage.js
 * Visual bracket for editing a user's picks — matches AdminResultsBracket layout.
 * Orange highlight = user's current pick. Green "W" = actual game winner.
 * Click a team to set/toggle pick. Cascade-clears downstream picks on change.
 * Single Save button commits everything at once.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';

const GAME_FEEDS_INTO = {
  'r64-s1':['r32-s1',0],'r64-s2':['r32-s1',1],'r64-s3':['r32-s2',0],'r64-s4':['r32-s2',1],
  'r64-s5':['r32-s3',0],'r64-s6':['r32-s3',1],'r64-s7':['r32-s4',0],'r64-s8':['r32-s4',1],
  'r64-e1':['r32-e1',0],'r64-e2':['r32-e1',1],'r64-e3':['r32-e2',0],'r64-e4':['r32-e2',1],
  'r64-e5':['r32-e3',0],'r64-e6':['r32-e3',1],'r64-e7':['r32-e4',0],'r64-e8':['r32-e4',1],
  'r64-m1':['r32-m1',0],'r64-m2':['r32-m1',1],'r64-m3':['r32-m2',0],'r64-m4':['r32-m2',1],
  'r64-m5':['r32-m3',0],'r64-m6':['r32-m3',1],'r64-m7':['r32-m4',0],'r64-m8':['r32-m4',1],
  'r64-w1':['r32-w1',0],'r64-w2':['r32-w1',1],'r64-w3':['r32-w2',0],'r64-w4':['r32-w2',1],
  'r64-w5':['r32-w3',0],'r64-w6':['r32-w3',1],'r64-w7':['r32-w4',0],'r64-w8':['r32-w4',1],
  'r32-s1':['r16-s1',0],'r32-s2':['r16-s1',1],'r32-s3':['r16-s2',0],'r32-s4':['r16-s2',1],
  'r32-e1':['r16-e1',0],'r32-e2':['r16-e1',1],'r32-e3':['r16-e2',0],'r32-e4':['r16-e2',1],
  'r32-m1':['r16-m1',0],'r32-m2':['r16-m1',1],'r32-m3':['r16-m2',0],'r32-m4':['r16-m2',1],
  'r32-w1':['r16-w1',0],'r32-w2':['r16-w1',1],'r32-w3':['r16-w2',0],'r32-w4':['r16-w2',1],
  'r16-s1':['r8-s',0],'r16-s2':['r8-s',1],'r16-e1':['r8-e',0],'r16-e2':['r8-e',1],
  'r16-m1':['r8-m',0],'r16-m2':['r8-m',1],'r16-w1':['r8-w',0],'r16-w2':['r8-w',1],
  'r4-1':['final-1',0],'r4-2':['final-1',1],
};

const FEEDER_GAMES = {
  'r32-s1':['r64-s1','r64-s2'],'r32-s2':['r64-s3','r64-s4'],'r32-s3':['r64-s5','r64-s6'],'r32-s4':['r64-s7','r64-s8'],
  'r32-e1':['r64-e1','r64-e2'],'r32-e2':['r64-e3','r64-e4'],'r32-e3':['r64-e5','r64-e6'],'r32-e4':['r64-e7','r64-e8'],
  'r32-m1':['r64-m1','r64-m2'],'r32-m2':['r64-m3','r64-m4'],'r32-m3':['r64-m5','r64-m6'],'r32-m4':['r64-m7','r64-m8'],
  'r32-w1':['r64-w1','r64-w2'],'r32-w2':['r64-w3','r64-w4'],'r32-w3':['r64-w5','r64-w6'],'r32-w4':['r64-w7','r64-w8'],
  'r16-s1':['r32-s1','r32-s2'],'r16-s2':['r32-s3','r32-s4'],
  'r16-e1':['r32-e1','r32-e2'],'r16-e2':['r32-e3','r32-e4'],
  'r16-m1':['r32-m1','r32-m2'],'r16-m2':['r32-m3','r32-m4'],
  'r16-w1':['r32-w1','r32-w2'],'r16-w2':['r32-w3','r32-w4'],
  'r8-s':['r16-s1','r16-s2'],'r8-e':['r16-e1','r16-e2'],'r8-m':['r16-m1','r16-m2'],'r8-w':['r16-w1','r16-w2'],
  'final-1':['r4-1','r4-2'],
};

function getR8Feed(rl, wiring) {
  const ff = wiring?.finalFour || { semi1:['South','West'], semi2:['East','Midwest'] };
  const s1 = [ff.semi1[0][0].toLowerCase(), ff.semi1[1][0].toLowerCase()];
  const s2 = [ff.semi2[0][0].toLowerCase(), ff.semi2[1][0].toLowerCase()];
  if (s1[0]===rl) return ['r4-1',0]; if (s1[1]===rl) return ['r4-1',1];
  if (s2[0]===rl) return ['r4-2',0]; if (s2[1]===rl) return ['r4-2',1];
  return null;
}

function cascadeClearPicks(gameId, oldTeamId, newPicks, wiring) {
  if (!oldTeamId) return;
  const rl = gameId.startsWith('r8-') ? gameId.replace('r8-','') : null;
  const entry = GAME_FEEDS_INTO[gameId] || (rl ? getR8Feed(rl, wiring) : null);
  if (!entry) return;
  const [nextId] = entry;
  // Only clear the next game's pick if it was the team we just removed
  if (newPicks[nextId] && String(newPicks[nextId]) === String(oldTeamId)) {
    const clearedTeamId = newPicks[nextId];
    delete newPicks[nextId];
    cascadeClearPicks(nextId, clearedTeamId, newPicks, wiring);
  }
}

function PickGameCard({ game, resolvedSlots, picks, playInPicks, onPick, changedGames, allPicks, gamesById = {} }) {
  if (!game) return <div style={{height:52}} />;
  const isPlayIn = game.round === 'playin';
  const currentPick = isPlayIn ? playInPicks[game.id] : picks[game.id];
  const actualWinner = game.winner;
  const isFinal = game.status === 'final';
  const isLive  = game.status === 'live';
  const isChanged = !!(changedGames && changedGames.has(game.id));

  const [homeTeam, awayTeam] = resolvedSlots || [game.homeTeam, game.awayTeam];

  const Team = ({ team }) => {
    const isTbd = !team || team.id === 'tbd' || String(team.id).includes('winner') || String(team.id).includes('playin');
    // Resolve pick ID — handles play-in game ID placeholders (already normalized ESPN IDs at load)
    const resolvedPick = (() => {
      if (!currentPick) return null;
      const sid = String(currentPick);
      // If it's a play-in game ID, resolve to the actual winner
      const pg = gamesById[sid];
      if (pg?.round === 'playin' && pg?.winner) return String(pg.winner);
      return sid;
    })();
    const isPicked = !isTbd && resolvedPick && (
      resolvedPick === String(team.id) ||
      String(currentPick) === String(team.id) ||
      (allPicks && String(allPicks[game.id]) === String(team.id)) ||
      // Fallback: numeric ESPN ID match via team.espnId
      (team.espnId && (String(currentPick) === String(team.espnId) || resolvedPick === String(team.espnId)))
    );
    const isWinner = !isTbd && actualWinner && String(actualWinner) === String(team.id);
    const canClick = !isTbd;
    const label = isTbd
      ? (team?.name && team.name !== 'TBD' ? team.name : 'TBD')
      : `(${team?.seed}) ${team?.name}`;

    return (
      <div
        onClick={() => canClick && onPick(game, isPicked ? null : team, isPlayIn)}
        style={{
          padding: '5px 8px', display:'flex', alignItems:'center', gap:4,
          cursor: canClick ? 'pointer' : 'default',
          background: isPicked ? 'rgba(230,126,34,0.18)' : 'transparent',
          borderLeft: isPicked ? '3px solid #e67e22' : '3px solid transparent',
          minHeight: 22,
        }}
        onMouseEnter={e => { if(canClick&&!isPicked) e.currentTarget.style.background='rgba(75,139,190,0.12)'; }}
        onMouseLeave={e => { if(!isPicked) e.currentTarget.style.background='transparent'; }}
      >
        <span style={{
          fontFamily:'var(--font-condensed)', fontSize:'0.78rem', flex:1,
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          color: isTbd ? '#3a5a80' : isPicked ? '#e67e22' : 'var(--cream)',
          fontWeight: isPicked ? 700 : 400,
        }}>{label}</span>
        {isPicked && <span style={{color:'#e67e22',fontSize:'0.65rem',flexShrink:0}}>&#x25CF;</span>}
        {isWinner && (
          <span style={{
            color:'#27ae60', fontSize:'0.6rem', flexShrink:0,
            background:'rgba(39,174,96,0.15)', borderRadius:3, padding:'1px 4px', marginLeft:2,
          }}>W</span>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background:'var(--navy-light)',
      border:`${isChanged?2:1}px solid ${isChanged?'#4b8bbe':currentPick?'rgba(230,126,34,0.45)':isFinal?'rgba(39,174,96,0.3)':'#2a4470'}`,
      borderRadius:5, overflow:'hidden', position:'relative', width:'100%',
      boxShadow: isChanged ? '0 0 6px rgba(75,139,190,0.35)' : 'none',
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
        padding:'1px 5px',
        background: isChanged ? 'rgba(75,139,190,0.18)' : 'rgba(0,0,0,0.2)',
        borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
        <span style={{fontSize:'0.58rem',fontFamily:'var(--font-condensed)',letterSpacing:'0.5px',
          color: isChanged?'#4b8bbe':isFinal?'rgba(39,174,96,0.7)':isLive?'#2ecc71':'#3a5a80'}}>
          {isChanged ? '\u2605 CHANGED' : isFinal ? 'FINAL' : isLive ? '\u25CF LIVE' : game.id}
        </span>
        {currentPick && (
          <button onClick={e=>{e.stopPropagation();onPick(game,null,isPlayIn);}}
            style={{background:'none',border:'none',color:'#3a5a80',cursor:'pointer',
              fontSize:'0.6rem',padding:'0 2px',lineHeight:1}} title="Clear pick">&#x2715;</button>
        )}
      </div>
      <Team team={homeTeam} />
      <div style={{height:1,background:'rgba(255,255,255,0.05)'}} />
      <Team team={awayTeam} />
    </div>
  );
}

function BracketRound({ ids, gamesById, picks, playInPicks, onPick, label, groupSize, resolveSlots, changedGames }) {
  const cardW = 175;
  const LABEL_H = 20; // height of the label row
  return (
    <div style={{width:cardW+16,flexShrink:0,display:'flex',flexDirection:'column'}}>
      <div style={{height:LABEL_H,textAlign:'center',fontFamily:'var(--font-condensed)',fontSize:'0.6rem',
        color:'#4a6a90',letterSpacing:'1px',marginBottom:4,textTransform:'uppercase'}}>{label}</div>
      <div style={{flex:1,display:'flex',flexDirection:'column'}}>
        {ids.map(gid => {
          const game = gamesById[gid];
          return (
            <div key={gid} style={{
              flex: groupSize, display:'flex', alignItems:'center', justifyContent:'center',
              paddingLeft:'8px', paddingRight:'8px',
            }}>
              <PickGameCard
                game={game}
                resolvedSlots={game ? resolveSlots(game) : null}
                picks={picks}
                playInPicks={playInPicks}
                onPick={onPick}
                changedGames={changedGames}
                allPicks={{...picks,...playInPicks}} gamesById={gamesById}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RegionBracket({ region, gamesById, picks, playInPicks, onPick, resolveSlots, changedGames }) {
  const rl = region[0].toLowerCase();
  const rounds = [
    { label:'R64',      ids:[1,2,3,4,5,6,7,8].map(n=>`r64-${rl}${n}`), size:1 },
    { label:'R32',      ids:[1,2,3,4].map(n=>`r32-${rl}${n}`), size:2 },
    { label:'SWEET 16', ids:[1,2].map(n=>`r16-${rl}${n}`), size:4 },
    { label:'ELITE 8',  ids:[`r8-${rl}`], size:8 },
  ];
  const LABEL_H = 24; // matches BracketRound label height + marginBottom
  const ROW_H = 58;
  const totalH = 8 * ROW_H;

  return (
    <div>
      <div style={{fontFamily:'var(--font-display)',fontSize:'0.9rem',color:'var(--blue)',
        letterSpacing:'2px',textAlign:'center',marginBottom:10}}>
        {region.toUpperCase()} REGION
      </div>
      <div style={{display:'flex',flexDirection:'row',height:totalH + LABEL_H,alignItems:'stretch'}}>
        {rounds.map(round => (
          <BracketRound key={round.label} ids={round.ids} gamesById={gamesById}
            picks={picks} playInPicks={playInPicks} onPick={onPick}
            label={round.label} groupSize={round.size} resolveSlots={resolveSlots}
            changedGames={changedGames} />
        ))}
      </div>
    </div>
  );
}

function PlayInPanel({ gamesById, picks, playInPicks, onPick, resolveSlots, changedGames, playInRoundName = 'First Four' }) {
  const playInIds = Object.keys(gamesById).filter(id => gamesById[id].round === 'playin').sort();
  if (!playInIds.length) return <div style={{color:'var(--gray)',padding:20}}>No Play-In games configured.</div>;
  return (
    <div>
      <div style={{fontFamily:'var(--font-display)',fontSize:'0.9rem',color:'var(--blue)',
        letterSpacing:'2px',marginBottom:16}}>{playInRoundName.toUpperCase()}</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:16}}>
        {playInIds.map(gid => {
          const game = gamesById[gid];
          return (
            <div key={gid} style={{width:200}}>
              <div style={{fontFamily:'var(--font-condensed)',fontSize:'0.62rem',
                color:'#4a6a90',marginBottom:4,textTransform:'uppercase'}}>{gid}</div>
              <PickGameCard
                game={game}
                resolvedSlots={game ? resolveSlots(game) : null}
                picks={picks}
                playInPicks={playInPicks}
                onPick={onPick}
                changedGames={changedGames}
                allPicks={{...picks,...playInPicks}} gamesById={gamesById}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FinalFourPanel({ gamesById, picks, playInPicks, onPick, wiring, resolveSlots, changedGames }) {
  const ff = wiring?.finalFour || { semi1:['South','West'], semi2:['East','Midwest'] };
  const cardW = 191;

  const Arrow = () => (
    <div style={{display:'flex',alignItems:'center',color:'#3a5a80',fontSize:'1.4rem',
      padding:'0 4px',alignSelf:'center',marginTop:18,flexShrink:0}}>&#x2192;</div>
  );

  return (
    <div>
      <div style={{fontFamily:'var(--font-display)',fontSize:'0.9rem',color:'var(--blue)',
        letterSpacing:'2px',marginBottom:12}}>FINAL FOUR + CHAMPIONSHIP</div>
      <div style={{display:'flex',flexDirection:'row',alignItems:'flex-start',gap:0,overflowX:'auto'}}>

        {/* Semi 1 */}
        <div style={{width:cardW,flexShrink:0}}>
          <div style={{textAlign:'center',fontFamily:'var(--font-condensed)',fontSize:'0.6rem',
            color:'#4a6a90',letterSpacing:'1px',marginBottom:4,textTransform:'uppercase'}}>
            {ff.semi1.join(' vs ')}
          </div>
          <div style={{padding:'0 8px'}}>
            <PickGameCard game={gamesById['r4-1']}
              resolvedSlots={gamesById['r4-1'] ? resolveSlots(gamesById['r4-1']) : null}
              picks={picks} playInPicks={playInPicks} onPick={onPick} changedGames={changedGames} allPicks={{...picks,...playInPicks}} gamesById={gamesById} />
          </div>
        </div>

        <Arrow />

        {/* Championship */}
        <div style={{width:cardW,flexShrink:0}}>
          <div style={{textAlign:'center',fontFamily:'var(--font-display)',fontSize:'0.72rem',
            color:'var(--blue)',letterSpacing:'2px',marginBottom:4}}>CHAMPIONSHIP</div>
          <div style={{padding:'0 8px'}}>
            <PickGameCard game={gamesById['final-1']}
              resolvedSlots={gamesById['final-1'] ? resolveSlots(gamesById['final-1']) : null}
              picks={picks} playInPicks={playInPicks} onPick={onPick} changedGames={changedGames} allPicks={{...picks,...playInPicks}} gamesById={gamesById} />
          </div>
        </div>

        <Arrow />

        {/* Semi 2 */}
        <div style={{width:cardW,flexShrink:0}}>
          <div style={{textAlign:'center',fontFamily:'var(--font-condensed)',fontSize:'0.6rem',
            color:'#4a6a90',letterSpacing:'1px',marginBottom:4,textTransform:'uppercase'}}>
            {ff.semi2.join(' vs ')}
          </div>
          <div style={{padding:'0 8px'}}>
            <PickGameCard game={gamesById['r4-2']}
              resolvedSlots={gamesById['r4-2'] ? resolveSlots(gamesById['r4-2']) : null}
              picks={picks} playInPicks={playInPicks} onPick={onPick} changedGames={changedGames} allPicks={{...picks,...playInPicks}} gamesById={gamesById} />
          </div>
        </div>

      </div>
    </div>
  );
}
export default function AdminBracketEditPage({ bracketId, onClose, wiring, playInRoundName = 'First Four' }) {
  const api = useApi();
  const [bracket, setBracket] = useState(null);
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState({});
  const [playInPicks, setPlayInPicks] = useState({});
  const [tiebreaker, setTiebreaker] = useState('');
  const [sleeperTeamId, setSleeperTeamId] = useState('');
  const [sleeperTeams, setSleeperTeams] = useState([]);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('playin');
  const regionOrder = wiring?.regionOrder || ['South','East','Midwest','West'];

  const load = useCallback(async () => {
    if (!bracketId) return;
    try {
      const [b, g, s, espnTeamsResp] = await Promise.all([
        api.adminGetBracket(bracketId),
        api.adminGetGames(),
        api.getSleeperTeams(),
        fetch((process.env.REACT_APP_API_URL || '/api') + '/espn-teams').then(r => r.json()).catch(() => ({ teams: {} })),
      ]);
      setBracket(b);
      setPicks({ ...(b.picks || {}) });

      // Normalize playInPicks: convert ESPN numeric IDs to slugs using espnTeams + game data
      const rawPlayInPicks = b.playInPicks || {};
      const gamesArr = Array.isArray(g) ? g : [];
      const espnTeams = espnTeamsResp?.teams || {};
      // Build ESPN ID → slug map from espnTeams cache
      const espnMap = {};
      Object.entries(espnTeams).forEach(([id, t]) => {
        if (t.slug) espnMap[String(id)] = t.slug;
        else if (t.id) espnMap[String(id)] = t.id;
      });
      // Also map from play-in game team objects — both espnId and numeric id fields
      gamesArr.filter(pg => pg.round === 'playin').forEach(pg => {
        [pg.homeTeam, pg.awayTeam].forEach(team => {
          if (!team?.id) return;
          if (team.espnId) espnMap[String(team.espnId)] = team.id;
          // If team.id is numeric (ESPN-seeded), map it to itself so it matches directly
          if (!isNaN(Number(team.id))) espnMap[String(team.id)] = String(team.id);
        });
      });
      // Normalize each playInPick
      const normalizedPlayInPicks = {};
      Object.entries(rawPlayInPicks).forEach(([gameId, pickId]) => {
        const sid = String(pickId);
        normalizedPlayInPicks[gameId] = espnMap[sid] || sid;
      });
      setPlayInPicks(normalizedPlayInPicks);
      setTiebreaker(b.tiebreaker != null ? String(b.tiebreaker) : '');
      setSleeperTeamId(b.sleeperTeamId || '');
      setSleeperTeams(s?.teams || []);
      setGames(Array.isArray(g) ? g : []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [bracketId]);

  useEffect(() => { load(); }, [load]);

  const gamesById = {};
  games.forEach(g => { gamesById[g.id] = g; });

  // Build ESPN numeric ID → slug map from play-in game team objects
  // Needed because playInPicks stores ESPN IDs but team.id is a slug
  const espnIdToSlug = {};
  games.filter(g => g.round === 'playin').forEach(g => {
    [g.homeTeam, g.awayTeam].forEach(team => {
      if (!team?.id) return;
      if (team.espnId) espnIdToSlug[String(team.espnId)] = team.id;
      // Also build from espnTeams cache if available via team abbrev matching
    });
  });

  function findTeamInGames(teamId) {
    if (!teamId) return null;
    // If teamId is a play-in game ID, resolve to the actual winner
    const playInGame = games.find(g => g.round === 'playin' && g.id === teamId);
    if (playInGame?.winner) {
      for (const g of games) {
        if (String(g.homeTeam?.id) === String(playInGame.winner)) return g.homeTeam;
        if (String(g.awayTeam?.id) === String(playInGame.winner)) return g.awayTeam;
      }
    }
    if (playInGame) {
      // Play-in game exists but no winner yet — show placeholder
      const t1 = playInGame.homeTeam, t2 = playInGame.awayTeam;
      const name = (t1 && t2 && t1.name && t2.name)
        ? `${t1.name.split(' ').pop()} / ${t2.name.split(' ').pop()}`
        : 'Play-In';
      return { id: teamId, name, seed: t1?.seed || t2?.seed || '?' };
    }
    for (const g of games) {
      if (String(g.homeTeam?.id) === String(teamId)) return g.homeTeam;
      if (String(g.awayTeam?.id) === String(teamId)) return g.awayTeam;
    }
    return { id: teamId, name: String(teamId), seed: '?' };
  }

  function resolvePickedTeamForGame(gameId) {
    const allP = { ...picks, ...playInPicks };
    const pickId = allP[gameId];
    if (!pickId) return null;
    return findTeamInGames(pickId);
  }

  function resolveSlots(game) {
    const home = game.homeTeam;
    const away = game.awayTeam;
    const isTbdTeam = (t) => !t || t.id === 'tbd' || String(t.id).includes('winner') || String(t.id).includes('playin');

    // Championship — always resolve from r4 picks regardless of what game object contains
    if (game.id === 'final-1') {
      const teamA = resolvePickedTeamForGame('r4-1') || { id:'tbd', name:'Semifinal 1 Winner', seed:'' };
      const teamB = resolvePickedTeamForGame('r4-2') || { id:'tbd', name:'Semifinal 2 Winner', seed:'' };
      return [teamA, teamB];
    }

    // R4 (Final Four semis) — always resolve from r8 picks via wiring
    if (game.round === 'R4') {
      const ff = wiring?.finalFour || { semi1:['South','West'], semi2:['East','Midwest'] };
      const regions = game.id === 'r4-1' ? ff.semi1 : ff.semi2;
      const [r1, r2] = regions.map(r => r[0].toLowerCase());
      const teamA = resolvePickedTeamForGame(`r8-${r1}`) || { id:'tbd', name:`${regions[0]} Champion`, seed:'' };
      const teamB = resolvePickedTeamForGame(`r8-${r2}`) || { id:'tbd', name:`${regions[1]} Champion`, seed:'' };
      return [teamA, teamB];
    }

    // R32, S16, E8 — always trace from feeder picks so user's selections show
    const feeders = FEEDER_GAMES[game.id];
    if (feeders) {
      const teamA = resolvePickedTeamForGame(feeders[0]) || { id:'tbd', name:'TBD', seed:'' };
      const teamB = resolvePickedTeamForGame(feeders[1]) || { id:'tbd', name:'TBD', seed:'' };
      return [teamA, teamB];
    }

    // R64 and play-in: resolve any play-in slots to actual winner
    const resolveSlot = (slot) => {
      if (!slot) return { id: 'tbd', name: 'TBD' };
      if (slot.playInGameId) {
        const pg = gamesById[slot.playInGameId];
        // Use actual winner if known
        if (pg?.winner) return findTeamInGames(pg.winner) || slot;
        // Show placeholder with team names
        if (pg?.homeTeam && pg?.awayTeam) {
          const abbr = t => t.name?.split(' ').pop() || t.abbrev || '?';
          return { id: slot.playInGameId, name: `${abbr(pg.homeTeam)} / ${abbr(pg.awayTeam)}`, seed: slot.seed };
        }
      }
      return slot;
    };
    if (!isTbdTeam(home) && !isTbdTeam(away)) return [home, away];
    return [resolveSlot(home), resolveSlot(away)];
  }

  // Change tracking - string comparisons throughout to avoid type mismatches
  const originalPicks = { ...(bracket?.picks || {}), ...(bracket?.playInPicks || {}) };
  const allCurrentPicks = { ...picks, ...playInPicks };
  const changedGames = new Set(
    Object.keys({ ...allCurrentPicks, ...originalPicks })
      .filter(gid => (allCurrentPicks[gid] ?? '') !== (originalPicks[gid] ?? ''))
  );
  const originalTb = bracket?.tiebreaker != null ? String(bracket.tiebreaker) : '';
  const tbChanged = tiebreaker !== originalTb;
  const sleeperChanged = sleeperTeamId !== (bracket?.sleeperTeamId || '');
  const totalChanged = changedGames.size + (tbChanged ? 1 : 0) + (sleeperChanged ? 1 : 0);

  const handlePick = useCallback((game, team, isPlayIn) => {
    const teamId = team ? String(team.id) : null;
    if (isPlayIn) {
      setPlayInPicks(prev => {
        const next = { ...prev };
        if (!teamId) delete next[game.id];
        else next[game.id] = teamId;
        return next;
      });
    } else {
      setPicks(prev => {
        const next = { ...prev };
        if (!teamId) {
          const oldTeamId = next[game.id];
          delete next[game.id];
          cascadeClearPicks(game.id, oldTeamId, next, wiring);
        } else {
          if (next[game.id] && next[game.id] !== teamId) {
            const oldTeamId = next[game.id];
            cascadeClearPicks(game.id, oldTeamId, next, wiring);
          }
          next[game.id] = teamId;
        }
        return next;
      });
    }
  }, [wiring]);

  async function handleSave() {
    setError(''); setStatus('Saving...');
    try {
      const result = await api.adminUpdateBracketPicks(bracketId, {
        picks, playInPicks,
        tiebreaker: tiebreaker !== '' ? Number(tiebreaker) : undefined,
        sleeperTeamId,
        note: note || 'Admin edit',
      });
      setStatus(`\u2705 Saved! New score: ${result.newScore} pts`);
      const b = await api.adminGetBracket(bracketId);
      setBracket(b);
      setTiebreaker(b.tiebreaker != null ? String(b.tiebreaker) : '');
    } catch(err) { setError(err.message); setStatus(''); }
  }

  function tabHasChanges(key) {
    if (key === 'playin') return [...changedGames].some(gid => gamesById[gid]?.round === 'playin');
    if (regionOrder.includes(key)) {
      const rl = key[0].toLowerCase();
      const ids = [
        ...[1,2,3,4,5,6,7,8].map(n=>`r64-${rl}${n}`),
        ...[1,2,3,4].map(n=>`r32-${rl}${n}`),
        ...[1,2].map(n=>`r16-${rl}${n}`),
        `r8-${rl}`,
      ];
      return ids.some(gid => changedGames.has(gid));
    }
    if (key === 'finals') return ['r4-1','r4-2','final-1'].some(gid => changedGames.has(gid));
    return false;
  }

  const tabs = [
    { key:'playin', label:playInRoundName.toUpperCase() },
    ...regionOrder.map(r => ({ key:r, label:r.toUpperCase() })),
    { key:'finals', label:'FINAL FOUR' },
  ];

  if (loading) return <div style={{padding:40,textAlign:'center',color:'var(--gray)'}}>Loading bracket...</div>;

  const totalPickCount = Object.keys(allCurrentPicks).length;
  // bracket.name = entry/bracket name, bracket.userName = the user's display name
  const entryName = bracket?.name || bracketId;

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <div style={{fontFamily:'var(--font-display)',fontSize:'1.1rem',color:'var(--cream)',letterSpacing:'1px'}}>
            Editing: {entryName}
          </div>
          {(bracket?.userName || bracket?.email) && (
            <div style={{fontFamily:'var(--font-condensed)',fontSize:'0.8rem',color:'var(--gray)',marginTop:2}}>
              {bracket.userName}{bracket.email ? ` \u00B7 ${bracket.email}` : ''}
            </div>
          )}
          <div style={{display:'flex',gap:12,marginTop:6,alignItems:'center'}}>
            <span style={{fontFamily:'var(--font-condensed)',fontSize:'0.78rem',color:'var(--gray)'}}>
              {totalPickCount} pick{totalPickCount!==1?'s':''} made
            </span>
            {totalChanged > 0 && (
              <span style={{fontFamily:'var(--font-condensed)',fontSize:'0.75rem',
                color:'#4b8bbe',background:'rgba(75,139,190,0.1)',padding:'2px 8px',borderRadius:10}}>
                ★ {totalChanged} unsaved change{totalChanged!==1?'s':''}
              </span>
            )}
          </div>
        </div>
        <button className="btn btn-secondary" onClick={onClose}>&larr; Back to Brackets</button>
      </div>

      {error && <div className="alert alert-error" style={{marginBottom:10}}>
        {error}
        <button onClick={()=>setError('')} style={{float:'right',background:'none',border:'none',color:'inherit',cursor:'pointer'}}>&#x2715;</button>
      </div>}
      {status && <div className="alert alert-success" style={{marginBottom:10}}>{status}</div>}

      {/* Controls card */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div className="form-group" style={{margin:0,width:140}}>
            <label className="form-label">Tiebreaker {tbChanged && <span style={{color:'#4b8bbe'}}>★</span>}</label>
            <input className="form-input" type="number" value={tiebreaker} min={0} max={500}
              onChange={e=>setTiebreaker(e.target.value)} placeholder="0-500"
              style={tbChanged ? {borderColor:'#4b8bbe',boxShadow:'0 0 0 1px #4b8bbe'} : {}} />
          </div>
          <div className="form-group" style={{margin:0,flex:2}}>
            <label className="form-label">&#x1F31F; Sleeper Team {sleeperChanged && <span style={{color:'#4b8bbe'}}>★</span>}</label>
            <select className="form-select" value={sleeperTeamId} onChange={e=>setSleeperTeamId(e.target.value)}
              style={sleeperChanged ? {borderColor:'#4b8bbe',boxShadow:'0 0 0 1px #4b8bbe'} : {}}>
              <option value="">Select sleeper...</option>
              {[...sleeperTeams]
                .sort((a,b)=>a.seed-b.seed||(a.region||'').localeCompare(b.region||''))
                .map(t=><option key={t.id} value={t.id}>({t.seed}) {t.name} &mdash; {t.region}</option>)}
            </select>
          </div>
          <div className="form-group" style={{margin:0,flex:2}}>
            <label className="form-label">Edit Note (optional)</label>
            <input className="form-input" value={note} onChange={e=>setNote(e.target.value)}
              placeholder="e.g. Corrected Florida pick per user request" />
          </div>
          <button className="btn btn-primary" onClick={handleSave} disabled={totalChanged===0}>
            &#x1F4BE; Save {totalChanged>0 ? `(${totalChanged} change${totalChanged!==1?'s':''})` : ''}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{fontFamily:'var(--font-condensed)',fontSize:'0.72rem',color:'var(--gray)',
        marginBottom:12,padding:'5px 10px',background:'rgba(75,139,190,0.06)',borderRadius:4,
        border:'1px solid rgba(75,139,190,0.12)',display:'flex',gap:16,flexWrap:'wrap'}}>
        <span>Click a team to set pick &mdash; click &#x2715; or same team to clear</span>
        <span style={{color:'#e67e22',fontWeight:700}}>&#x25CF; Orange = current pick</span>
        <span style={{color:'#27ae60',fontWeight:700}}>W = actual winner</span>
        <span style={{color:'#4b8bbe',fontWeight:700}}>★ Blue = changed</span>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{marginBottom:18,flexWrap:'wrap'}}>
        {tabs.map(t => {
          let tabPickCount = 0;
          if (t.key==='playin') {
            tabPickCount = Object.keys(playInPicks).length;
          } else if (regionOrder.includes(t.key)) {
            const rl = t.key[0].toLowerCase();
            const regionGameIds = [
              ...[1,2,3,4,5,6,7,8].map(n=>`r64-${rl}${n}`),
              ...[1,2,3,4].map(n=>`r32-${rl}${n}`),
              ...[1,2].map(n=>`r16-${rl}${n}`),
              `r8-${rl}`,
            ];
            tabPickCount = regionGameIds.filter(gid=>picks[gid]).length;
          } else if (t.key==='finals') {
            tabPickCount = ['r4-1','r4-2','final-1'].filter(gid=>picks[gid]).length;
          }
          const hasChanges = tabHasChanges(t.key);
          return (
            <button key={t.key} className={`tab${activeTab===t.key?' active':''}`}
              onClick={()=>setActiveTab(t.key)} style={{fontSize:'0.78rem'}}>
              {t.label}
              {hasChanges
                ? <span style={{marginLeft:4,fontSize:'0.65rem',color:'#4b8bbe',fontWeight:700}}>★</span>
                : tabPickCount>0
                  ? <span style={{marginLeft:4,fontSize:'0.62rem',color:'#e67e22',fontWeight:700}}>{tabPickCount}</span>
                  : null
              }
            </button>
          );
        })}
      </div>

      {/* Bracket content */}
      <div style={{overflowX:'auto',paddingBottom:16}}>
        {activeTab==='playin' && (
          <PlayInPanel gamesById={gamesById} picks={picks} playInPicks={playInPicks}
            onPick={handlePick} resolveSlots={resolveSlots} changedGames={changedGames}
            playInRoundName={playInRoundName} />
        )}
        {regionOrder.includes(activeTab) && (
          <RegionBracket region={activeTab} gamesById={gamesById}
            picks={picks} playInPicks={playInPicks} onPick={handlePick} resolveSlots={resolveSlots} changedGames={changedGames} />
        )}
        {activeTab==='finals' && (
          <FinalFourPanel gamesById={gamesById} picks={picks} playInPicks={playInPicks}
            onPick={handlePick} wiring={wiring} resolveSlots={resolveSlots} changedGames={changedGames} />
        )}
      </div>
    </div>
  );
}
